import { useMutation, useQueryClient } from '@tanstack/react-query';
import { INPUTS_QUERY_KEY, INPUT_BY_ID_QUERY_KEY } from 'constants/queryKeys';
import { IInput } from 'interfaces/IInput';
import InputService from 'services/InputService';

interface UseUpdateInputHeaderArgs {
  activeOrganisationId: string | null;
}

interface UpdateInputHeaderContext {
  /** The plan as it was before this save, to put back if the save fails. */
  previousInput: IInput | null;
}

/**
 * Saves a plan's own fields (crop, variety, dates, area) on any plan — not
 * only the one open in Planning — and keeps the org's inputs cache in step.
 * Tasks are stripped from the payload, as Planning's header save does: the
 * backend leaves them alone, and they're saved one at a time with
 * useUpsertInputTask.
 *
 * The change shows straight away and is put back if the save fails. On
 * success the server's copy of the plan replaces the cached one, keeping
 * the cached tasks, so a task save in flight isn't undone by a stale copy.
 */
export const useUpdateInputHeader = ({
  activeOrganisationId,
}: UseUpdateInputHeaderArgs) => {
  const queryClient = useQueryClient();
  const queryKey = [INPUTS_QUERY_KEY, activeOrganisationId];

  // Only the plan's own fields are written — its cached tasks are kept, as
  // they're saved separately and may have changed since.
  const writeInput = (next: IInput) =>
    queryClient.setQueryData<IInput[]>(queryKey, (current) =>
      current?.map((input) =>
        input.id === next.id ? { ...next, tasks: input.tasks } : input
      )
    );

  return useMutation<
    Awaited<ReturnType<typeof InputService.UpdateDoc>>,
    unknown,
    IInput,
    UpdateInputHeaderContext
  >({
    mutationFn: async (input) => {
      const clientNow = new Date().toISOString();
      return InputService.UpdateDoc({ ...input, tasks: [] }, clientNow);
    },
    onMutate: async (input) => {
      if (!activeOrganisationId) return { previousInput: null };
      // A refetch landing mid-save would overwrite the change shown below.
      await queryClient.cancelQueries({ queryKey });
      const previousInput =
        queryClient
          .getQueryData<IInput[]>(queryKey)
          ?.find((item) => item.id === input.id) ?? null;
      writeInput(input);
      return { previousInput };
    },
    onError: (_error, _input, context) => {
      if (activeOrganisationId && context?.previousInput) {
        writeInput(context.previousInput);
      }
    },
    onSuccess: (response, input) => {
      const updatedInput = response?.data?.[0];
      if (!updatedInput || !activeOrganisationId) return;
      writeInput(updatedInput);
      // A Preview tab reads the single-plan cache — update it directly so it
      // reflects the header change without a refetch.
      queryClient.setQueryData<IInput>(
        [INPUT_BY_ID_QUERY_KEY, input.id],
        (current) => (current ? { ...updatedInput, tasks: current.tasks } : current)
      );
    },
  });
};

export default useUpdateInputHeader;
